/**
 * 磁力计校准的**结论**解释：`MAG_CAL_STATUS` 与 `fitness`。
 *
 * ## 为什么这一段值得单独一个文件
 *
 * 磁力计校准是唯一一种"过程与结论都从遥测里来"的校准，也是用户最容易失败的一种。
 * `cal_status` 有 11 个取值，其中 6 个是**具体**的失败原因（方向不对、半径不对、
 * 偏移过大、缩放不对、残差过大）。把它们统统显示成"校准失败"是最糟的处理方式：
 * 用户唯一能做的就是再试一次 —— 而方向不对需要的是调安装方向，半径不对需要的是
 * 换地方，两者的下一步完全相反。
 *
 * 所以这里把每个取值映射成三样东西：
 *   - 是不是终态（`running`）；
 *   - 一句中文原因（**原因**，不是"失败了"）；
 *   - 一句下一步建议（要具体到"做什么动作"）。
 *
 * 枚举名一律从 `@hgc/mavlink` 的生成表里查（`MAG_CAL_STATUS`），
 * 数字写在这个文件里一次都不出现 —— 抄错 `cal_status` 的值会让"半径不对"
 * 显示成"成功"，这类错误在真机上无法从现象反推。
 */

import { enumEntryByValue } from '@hgc/mavlink';

/** 失败原因的分类。界面按它决定图标/颜色，也按它决定要不要显示"重试"按钮。 */
export type MagCalFailureKind =
  | 'mag-generic'
  | 'mag-orientation'
  | 'mag-radius'
  | 'mag-offsets'
  | 'mag-scaling'
  | 'mag-residuals'
  | 'mag-not-started'
  | 'mag-unknown';

export interface MagCalStatusInfo {
  /** `MAG_CAL_STATUS` 原值 */
  readonly value: number;
  /** 枚举名；查不到时为 `null` */
  readonly name: string | null;
  /** 是否仍在进行（`MAG_CAL_WAITING_TO_START` / `RUNNING_STEP_ONE/TWO`） */
  readonly running: boolean;
  readonly success: boolean;
  /** `success === false && running === false` 时的失败分类 */
  readonly failure: MagCalFailureKind | null;
  /** 面向飞手的中文原因 */
  readonly reason: string;
  /** 下一步该做什么；没有可行建议时为 `null` */
  readonly advice: string | null;
}

/**
 * 逐条文案表，键是上游枚举名。
 *
 * 括号里的英文是上游 `common.xml` 对该取值的原文描述（便于和上游文档对照，
 * 排查"是不是我们理解错了"时用）。
 */
const STATUS_TABLE: Readonly<
  Record<
    string,
    {
      readonly running: boolean;
      readonly success: boolean;
      readonly failure: MagCalFailureKind | null;
      readonly reason: string;
      readonly advice: string | null;
    }
  >
> = {
  MAG_CAL_NOT_STARTED: {
    running: false,
    success: false,
    failure: 'mag-not-started',
    reason: '飞控报告这项罗盘校准根本没有开始',
    advice:
      '确认这个罗盘没有被禁用、且是健康的（COMPASS_USE / COMPASS_USE2 / COMPASS_USE3 为 1），然后重新发起校准。',
  },
  MAG_CAL_WAITING_TO_START: {
    running: true,
    success: false,
    failure: null,
    reason: '等待开始（飞控在等延时结束或传感器就绪）',
    advice: null,
  },
  MAG_CAL_RUNNING_STEP_ONE: {
    running: true,
    success: false,
    failure: null,
    reason: '正在进行第一步（拟合球面，消除偏移）',
    advice: null,
  },
  MAG_CAL_RUNNING_STEP_TWO: {
    running: true,
    success: false,
    failure: null,
    reason: '正在进行第二步（拟合椭球，消除缩放与轴间偏移）',
    advice: null,
  },
  MAG_CAL_SUCCESS: {
    running: false,
    success: true,
    failure: null,
    reason: '校准成功',
    advice: null,
  },
  MAG_CAL_FAILED: {
    running: false,
    success: false,
    failure: 'mag-generic',
    reason: '飞控报告校准失败，但没有给出更具体的原因',
    advice:
      '把机体搬到远离金属与磁源的空地重试一次；反复失败请检查罗盘安装位置是否紧贴动力线或磁钢。',
  },
  MAG_CAL_FAILED_ORIENTATION: {
    running: false,
    success: false,
    failure: 'mag-orientation',
    // 上游原文："the vehicle orientation is outside the required tolerance"
    reason: '罗盘方向不对（ORIENTATION）与实测磁场不符',
    advice:
      '别急着重试 —— 这一类是**安装方向**错了：把机体摆在已知航向（例如机头朝北）读取罗盘读数核对，必要时手动设置 COMPASS_ORIENT（外置罗盘还要看 COMPASS_ORIENT2/3）。',
  },
  MAG_CAL_FAILED_RADIUS: {
    running: false,
    success: false,
    failure: 'mag-radius',
    // 上游原文："the radius of the fitted sphere is unrealistically small or large"
    reason: '拟合出的磁场球半径不合理（过小或过大）',
    advice:
      '换一个远离磁干扰的位置重试，并确认机体附近没有磁铁、备用电池、磁性工具；半径过小常是周围有强磁源，过大常是罗盘读数量程/接线异常。',
  },
  MAG_CAL_FAILED_OFFSETS: {
    running: false,
    success: false,
    failure: 'mag-offsets',
    // 上游原文："offset magnitude too large"
    reason: '算出来的偏移量过大（超过飞控允许的上限）',
    advice:
      '机体的硬磁干扰太强：把罗盘尽量远离电源线、电调、磁钢（外置罗盘用支杆抬高），然后重做校准。',
  },
  MAG_CAL_FAILED_DIAG_SCALING: {
    running: false,
    success: false,
    failure: 'mag-scaling',
    // 上游原文："diagonal or off-diagonal scaling values out of valid range"
    reason: '缩放值（对角/非对角）超出合理范围',
    advice:
      '通常是磁干扰或罗盘硬件异常：先换位置重试，仍然失败就检查这个罗盘的接线与供电（软磁干扰大、或读数被其它传感器串扰）。',
  },
  MAG_CAL_FAILED_RESIDUALS_HIGH: {
    running: false,
    success: false,
    failure: 'mag-residuals',
    // 上游原文："fitness (RMS residual) exceeds tolerance"
    reason: '拟合残差（fitness）过大 —— 也就是**周围有磁干扰**或转动不充分',
    advice:
      '离开钢筋混凝土/钢筋台面、远离金属物体与带电设备，重新校准；转动时保证低速平缓、各方向都真的转到（只在一个平面里画圈最容易失败）。',
  },
};

const UNKNOWN: MagCalStatusInfo = Object.freeze({
  value: -1,
  name: null,
  running: false,
  success: false,
  failure: 'mag-unknown',
  reason: '飞控报了一个本仓库不认识的校准状态值',
  advice:
    '不要把它当成成功：请在日志里找到这帧 MAG_CAL_REPORT 的原始值，并升级本仓库的协议表（或升级固件后重试）。',
});

/**
 * `MAG_CAL_STATUS` 原值 → 结论。
 *
 * 认不出的值**不猜**：一律按失败处理并把原值带出来。把未知值当成功会让用户
 * 带着一个没校好的罗盘起飞 —— 这是不可接受的误报方向。
 */
export function magCalStatusInfo(value: number): MagCalStatusInfo {
  const entry = enumEntryByValue('MAG_CAL_STATUS', value);
  if (entry === null || entry === undefined) {
    return { ...UNKNOWN, value };
  }
  const mapped = STATUS_TABLE[entry.name];
  if (mapped === undefined) {
    // 上游新增了取值而本文件还没跟上：明说"没收录"，并给出枚举名对照
    return {
      value,
      name: entry.name,
      running: false,
      success: false,
      failure: 'mag-unknown',
      reason: `飞控返回 ${entry.name}（本文件未收录这个状态的处理方式，按失败处理）`,
      advice: UNKNOWN.advice,
    };
  }
  return {
    value,
    name: entry.name,
    running: mapped.running,
    success: mapped.success,
    failure: mapped.failure,
    reason: mapped.reason,
    advice: mapped.advice,
  };
}

/* ────────────────────────────── fitness ────────────────────────────── */

/** 经验上的"拟合干净"上限（ArduPilot 社区与厂家文档都按 10 mgauss 上下描述）。 */
const FITNESS_GOOD = 10;
/** 明显有干扰的量级 —— 到这个数基本可以确定环境里有磁干扰源。 */
const FITNESS_POOR = 30;

export type FitnessVerdict =
  /** 拟合干净 */
  | 'good'
  /** 达标：没超过飞控的阈值 */
  | 'acceptable'
  /** 偏高：超过阈值（但没到明显干扰的量级） */
  | 'high'
  /** 过大：几乎可以确定存在磁干扰 */
  | 'excessive';

export interface FitnessAssessment {
  readonly value: number;
  readonly verdict: FitnessVerdict;
  /** 这次判定用的阈值（来自 `COMPASS_CAL_FIT`，读不到时用 ArduPilot 默认值） */
  readonly tolerance: number;
  /** 阈值是不是飞控实测值（`false` 表示用的是默认值 16） */
  readonly toleranceFromVehicle: boolean;
  /** 面向飞手的一句话 */
  readonly text: string;
}

/**
 * fitness 的量级依据。
 *
 * `fitness` 是 `MAG_CAL_REPORT` 的 RMS 残差（单位 mgauss），飞控自己算的是
 * `sqrtf(_fitness)`（`CompassCalibrator::update_cal_report()`），
 * 判定线是 `_fitness > sq(_tolerance)` → `FAILED_RESIDUALS_HIGH`
 * （`CompassCalibrator::fit_acceptable_status()`），
 * 而这个 `tolerance` 就是参数 `COMPASS_CAL_FIT`，默认
 * `AP_COMPASS_CALIBRATION_FITNESS_DEFAULT = 16.0f`（`AP_Compass.h`）。
 *
 * 我们在这里给出四档，只有第 2 档（"达标"）是有据可依的硬判据：
 * 第 1、3、4 档是**经验分档**，用来把"高但没超阈值"和"高到基本可以确定是干扰"
 * 区分开，好让界面说人话。文案里会把实际用的阈值一起写出来，不让用户以为
 * 有个统一的、与固件无关的合格线。
 */
export function assessFitness(value: number, tolerance: number, fromVehicle = false): FitnessAssessment {
  const limit = Number.isFinite(tolerance) && tolerance > 0 ? tolerance : 16;
  const verdict: FitnessVerdict =
    value <= FITNESS_GOOD && value <= limit
      ? 'good'
      : value <= limit
        ? 'acceptable'
        : value <= FITNESS_POOR
          ? 'high'
          : 'excessive';
  const rounded = Math.round(value * 10) / 10;
  const limitText = `${Math.round(limit * 10) / 10}${fromVehicle ? '（来自飞控的 COMPASS_CAL_FIT）' : '（ArduPilot 默认值）'}`;
  switch (verdict) {
    case 'good':
      return {
        value,
        verdict,
        tolerance: limit,
        toleranceFromVehicle: fromVehicle,
        text: `fitness ${rounded} mgauss：拟合干净`,
      };
    case 'acceptable':
      return {
        value,
        verdict,
        tolerance: limit,
        toleranceFromVehicle: fromVehicle,
        text: `fitness ${rounded} mgauss：达标（在阈值 ${limitText} 之内）`,
      };
    case 'high':
      return {
        value,
        verdict,
        tolerance: limit,
        toleranceFromVehicle: fromVehicle,
        text: `fitness ${rounded} mgauss：偏高（已超过阈值 ${limitText}），拟合残差偏大，通常是附近有磁干扰`,
      };
    default:
      return {
        value,
        verdict,
        tolerance: limit,
        toleranceFromVehicle: fromVehicle,
        text: `fitness ${rounded} mgauss：过大（阈值 ${limitText}）—— 这个量级基本可以确定周围有磁干扰或铁磁结构`,
      };
  }
}
